import {
  Bot, BrainCircuit, Code, Heart, Database, Send, Plug, CreditCard,
  BarChart3, LineChart, PieChart, TrendingUp, Layers,
  SlidersHorizontal, Smartphone, GitBranch,
  Megaphone, Target, Play,
  Flame, Bell, CircleDot, Mail,
  Pen, Palette, FileText, MessageSquare,
  Triangle, Github,
  type LucideIcon,
} from "lucide-react";
import ParticleNetwork from "./ParticleNetwork";

const toolStack: { category: string; tools: { name: string; icon: LucideIcon }[] }[] = [
  {
    category: "AI & Development",
    tools: [
      { name: "Claude", icon: Bot },
      { name: "GPT", icon: BrainCircuit },
      { name: "Replit", icon: Code },
      { name: "Lovable", icon: Heart },
      { name: "Supabase", icon: Database },
      { name: "Vercel", icon: Triangle },
      { name: "GitHub", icon: Github },
      { name: "Stripe", icon: CreditCard },
      { name: "APIs", icon: Plug },
    ],
  },
  {
    category: "Analytics & Data",
    tools: [
      { name: "Amplitude", icon: BarChart3 },
      { name: "PostHog", icon: LineChart },
      { name: "Plausible", icon: PieChart },
      { name: "GA4", icon: TrendingUp },
      { name: "Heap", icon: Layers },
    ],
  },
  {
    category: "Attribution & Mobile",
    tools: [
      { name: "Adjust", icon: SlidersHorizontal },
      { name: "AppsFlyer", icon: Smartphone },
      { name: "Branch", icon: GitBranch },
    ],
  },
  {
    category: "Advertising",
    tools: [
      { name: "Meta Ads", icon: Megaphone },
      { name: "Google Ads", icon: Target },
      { name: "TikTok Ads", icon: Play },
    ],
  },
  {
    category: "Lifecycle & CRM",
    tools: [
      { name: "Braze", icon: Flame },
      { name: "OneSignal", icon: Bell },
      { name: "HubSpot", icon: CircleDot },
      { name: "Mailchimp", icon: Mail },
      { name: "Resend", icon: Send },
    ],
  },
  {
    category: "Design & Ops",
    tools: [
      { name: "Figma", icon: Pen },
      { name: "Canva", icon: Palette },
      { name: "Notion", icon: FileText },
      { name: "Slack", icon: MessageSquare },
    ],
  },
];

const AIBuilderSection = () => {
  return (
    <section id="ai-builder" className="section-padding bg-background relative overflow-hidden">
      {/* Faint code-grid background */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none font-mono text-[10px] leading-5 text-foreground whitespace-pre-wrap break-all select-none p-8 overflow-hidden">
        {`function buildGrowthEngine() { const systems = [acquisition, retention, monetization]; return systems.map(s => optimize(s, { ai: true })); } // I build the things I recommend `.repeat(40)}
      </div>

      <div className="content-max relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Callout card */}
          <div className="border border-primary/30 rounded-xl p-8 md:p-12 lg:p-16 bg-secondary/50 backdrop-blur-sm relative overflow-hidden">
            <ParticleNetwork />
            {/* Accent corner glow */}
            <div className="absolute top-0 left-0 w-32 h-32 bg-primary/5 rounded-br-full pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-primary/5 rounded-tl-full pointer-events-none" />

            <h2 className="font-headline text-3xl sm:text-4xl md:text-5xl text-foreground tracking-tight mb-10 relative">
              I Don't Just Strategize —{" "}
              <span className="font-mono text-primary text-2xl sm:text-3xl md:text-4xl">
                I Build
              </span>
            </h2>

            <div className="space-y-6 font-body text-base md:text-lg text-muted-foreground leading-relaxed relative">
              <p className="text-foreground/90 text-lg md:text-xl font-medium">
                Most growth consultants hand you a deck. I hand you systems.
              </p>
              <p>
                I've been deep in AI since 2022 — co-founding an
                Apple-recognized AI app, building custom tools from scratch, and
                integrating AI into every layer of the growth stack. I vibe code
                production apps using Replit, Lovable, and Claude. I build
                automations, internal tools, and AI-augmented workflows that
                compound your team's output.
              </p>
              <p>
                This isn't an add-on to my consulting. It's the foundation.
                Every engagement benefits from the fact that I can{" "}
                <span className="font-mono text-sm md:text-base text-primary font-medium border-b border-primary/40 pb-0.5">
                  build the things I recommend
                </span>{" "}
                — not just describe them.
              </p>
            </div>

            {/* Tool stack */}
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-5">
              {toolStack.map((group) => (
                <div key={group.category}>
                  <p className="font-mono text-[11px] uppercase tracking-[0.14em] text-muted-foreground mb-2.5">
                    {group.category}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {group.tools.map((tool) => (
                      <span
                        key={tool.name}
                        className="inline-flex items-center gap-1.5 font-body text-xs px-3 py-1.5 rounded-md bg-[hsl(240_5%_17%)] text-foreground/80 transition-all duration-200 hover:bg-primary/15 hover:text-primary hover:scale-105 cursor-default"
                      >
                        <tool.icon size={16} className="shrink-0 text-foreground/60" />
                        {tool.name}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AIBuilderSection;
